import clsx from 'clsx';
import { useRef } from 'react';
import useBoolean from '../../../hooks/useBoolean';
import useClickOutside from '../../../hooks/useClickOutside';
import { SelectOption } from '../types';

interface SelectProps {
  onClickOption?: (value: string) => void;
  options: SelectOption[];
  defaultValue: SelectOption;
  disabled?: boolean;
  className?: string;
}

const Select = ({
  onClickOption,
  options,
  defaultValue,
  disabled = false,
  className,
}: SelectProps) => {
  const optionsRef = useRef(null);
  const [isOpenOptions, { toggle: toggleOptions, setFalse: hideOptions }] =
    useBoolean(false);

  const handleShowOptions = () => {
    if (!disabled) toggleOptions();
    else hideOptions();
  };

  useClickOutside(optionsRef, hideOptions);

  const handleClick = (option: SelectOption) => {
    toggleOptions();
    if (onClickOption) {
      onClickOption(option.id);
    }
  };

  return (
    <div ref={optionsRef} className={clsx('md:w-full relative', className)}>
      <button
        onClick={handleShowOptions}
        className={clsx(
          'w-full flex justify-between z-10 items-center px-2.5 py-2 text-sm font-medium text-gray-500 bg-primary-fill rounded-none focus:outline-none',
          disabled ? 'cursor-not-allowed' : ''
        )}
        disabled={disabled}
        type="button"
      >
        <div className="flex items-center">
          {defaultValue.icon}
          {defaultValue.value}
        </div>
        <svg
          aria-hidden="true"
          className="w-4 h-4 ml-1"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div
        id="dropdown-states"
        className={clsx(
          'z-10 w-full bg-primary-fill divide-y divide-gray-100 rounded shadow absolute',
          isOpenOptions ? undefined : 'hidden'
        )}
      >
        <ul
          className="py-1 text-sm text-gray-700 w-full"
          aria-labelledby="states-button"
        >
          {options.map((option) => (
            <li key={option.id}>
              <button
                disabled={option.isDisabled}
                onClick={() => handleClick(option)}
                type="button"
                className={clsx(
                  'inline-flex w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100',
                  option.isDisabled
                    ? 'cursor-not-allowed text-[var(--colors-neutral-300)] bg-[var(--colors-neutral-200)]'
                    : ''
                )}
              >
                <div className="inline-flex items-center">
                  {option.icon}
                  {option.value}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Select;
